import { z } from 'zod';

import { CashLocationTypeSchema } from './cash-locations.schema.js';
import { CashSessionCloseModeSchema, CashSessionStatusSchema } from './cash-sessions.schema.js';
import {
  CashVarianceActionStatusSchema,
  CashVarianceActionTypeSchema,
  CashVarianceCaseStatusSchema,
  CashVarianceStageSchema,
} from './cash-variance.schema.js';
import {
  cashReportLocationScopeValues,
  cashReportMovementCategoryValues,
  cashReportPhysicalEffectValues,
  cashReportSourceTypeValues,
  type CashReportActorRefDto,
  type CashReportCashLocationRefDto,
  type CashReportCategoryBreakdownDto,
  type CashReportLocationScope,
  type CashReportMovementCategory,
  type CashReportPhysicalEffect,
  type CashReportPurchasePayoutRefDto,
  type CashReportSessionRefDto,
  type CashReportSourceRefDto,
  type CashReportSourceType,
  type CashReportVarianceActionRefDto,
  type CashReportVarianceCaseRefDto,
} from './cash-report-common.dto.js';

export const cashReportTimestampSchema = z.iso.datetime();
export const cashReportMonthSchema = z
  .string()
  .regex(/^\d{4}-\d{2}$/, 'Expected month in YYYY-MM format');
export const cashReportLocalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected localDate in YYYY-MM-DD format');

export function validateCashReportDateRange(
  value: { from?: string; to?: string },
  context: z.RefinementCtx,
): void {
  if (value.from && value.to && value.from > value.to) {
    context.addIssue({
      code: 'custom',
      message: 'from must be before or equal to to.',
      path: ['to'],
    });
  }
}

export function validateCashReportMonthRange(
  value: { fromMonth: string; toMonth: string },
  context: z.RefinementCtx,
): void {
  if (value.fromMonth > value.toMonth) {
    context.addIssue({
      code: 'custom',
      message: 'fromMonth must be before or equal to toMonth.',
      path: ['toMonth'],
    });
  }
}

export const CashReportLocationScopeSchema: z.ZodType<CashReportLocationScope> = z.enum(
  cashReportLocationScopeValues,
);
export const CashReportMovementCategorySchema: z.ZodType<CashReportMovementCategory> = z.enum(
  cashReportMovementCategoryValues,
);
export const CashReportPhysicalEffectSchema: z.ZodType<CashReportPhysicalEffect> = z.enum(
  cashReportPhysicalEffectValues,
);
export const CashReportSourceTypeSchema: z.ZodType<CashReportSourceType> = z.enum(
  cashReportSourceTypeValues,
);

export const CashReportActorRefDtoSchema: z.ZodType<CashReportActorRefDto> = z.object({
  userId: z.uuid(),
  displayName: z.string().trim().min(1).max(160).nullable(),
  email: z.email().nullable(),
  employeeId: z.uuid().nullable(),
  employeeNumber: z.string().trim().min(1).max(80).nullable(),
});

export const CashReportCashLocationRefDtoSchema: z.ZodType<CashReportCashLocationRefDto> = z.object(
  {
    cashLocationId: z.uuid(),
    kind: CashLocationTypeSchema,
    scope: CashReportLocationScopeSchema,
    name: z.string().trim().min(1).max(200).nullable(),
    storeId: z.uuid().nullable(),
    storeName: z.string().trim().min(1).max(160).nullable(),
    locationId: z.uuid().nullable(),
    locationName: z.string().trim().min(1).max(160).nullable(),
    stationId: z.uuid().nullable(),
    stationName: z.string().trim().min(1).max(160).nullable(),
  },
);

export const CashReportSessionRefDtoSchema: z.ZodType<CashReportSessionRefDto> = z.object({
  cashSessionId: z.uuid(),
  status: CashSessionStatusSchema,
  openedAt: cashReportTimestampSchema,
  closedAt: cashReportTimestampSchema.nullable(),
  closeMode: CashSessionCloseModeSchema.nullable(),
  displayName: z.string().trim().min(1).max(160).nullable(),
});

export const CashReportSourceRefDtoSchema: z.ZodType<CashReportSourceRefDto> = z.object({
  sourceType: CashReportSourceTypeSchema,
  sourceId: z.string().trim().min(1).max(200),
  displayName: z.string().trim().min(1).max(200).nullable(),
  occurredAt: cashReportTimestampSchema.nullable(),
});

export const CashReportVarianceCaseRefDtoSchema: z.ZodType<CashReportVarianceCaseRefDto> = z.object(
  {
    caseId: z.uuid(),
    stage: CashVarianceStageSchema.nullable(),
    status: CashVarianceCaseStatusSchema.nullable(),
  },
);

export const CashReportVarianceActionRefDtoSchema: z.ZodType<CashReportVarianceActionRefDto> =
  z.object({
    actionId: z.uuid(),
    actionType: CashVarianceActionTypeSchema,
    status: CashVarianceActionStatusSchema.nullable(),
  });

export const CashReportPurchasePayoutRefDtoSchema: z.ZodType<CashReportPurchasePayoutRefDto> =
  z.object({
    purchaseId: z.uuid(),
    folioNumber: z.number().int().positive().nullable(),
    committedAt: cashReportTimestampSchema.nullable(),
    cashSessionId: z.uuid().nullable(),
    cashLocationId: z.uuid().nullable(),
    paidAmountMinor: z.number().int().nonnegative(),
  });

export const CashReportCategoryBreakdownDtoSchema: z.ZodType<CashReportCategoryBreakdownDto> =
  z.object({
    reportCategory: CashReportMovementCategorySchema,
    totalInMinor: z.number().int().nonnegative(),
    totalOutMinor: z.number().int().nonnegative(),
    netAmountMinor: z.number().int(),
    movementCount: z.number().int().nonnegative(),
  });
